import React, { useCallback, useMemo, useState } from "react";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMaterialReactTable
} from "material-react-table";
import { Paper, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FleetManagementRow } from "../../FleetManagement/types/FleetManagement-row.types";
import { renderFuelCell } from "../../FleetManagement/FleetManagementTable.partial";
import { renderFleetPropertiesCell } from "../../FleetManagement/EventDetailDialog/partial/FleetManagementEventDetailTable.partial";
import { renderTableItemMultiImages } from "../../Common/ListTablePartial/ListTablePartial.partial";
import { capacityPerformanceWorkplace } from "../../../features/Reports/typings/reports.types";
import { EnlargedImageCarouselDialog } from "../../../features/EnlargedImageCarousel/enlarged-image-carousel-dialog.component";
import { commonLocalization } from "../../../functions/tableLocalization";
import { FleetFuelType } from "../../FleetManagement/types/FleetManagement.types";
import { withSeeditMrtInitialState } from "../../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../../themes/use-seedit-mrt-table-options";

export type FleetReportRow = FleetManagementRow & { utilization?: number };

function rowSelectionToIds(selection: MRT_RowSelectionState): number[] {
  return Object.keys(selection)
    .filter(key => selection[key])
    .map(Number);
}

export function compareFleetReportRows(
  a: FleetReportRow,
  b: FleetReportRow,
  selectedVehicleIds: Set<number>
): number {
  const aSelected = selectedVehicleIds.has(a.id);
  const bSelected = selectedVehicleIds.has(b.id);

  if (aSelected !== bSelected) return aSelected ? -1 : 1;

  const aUtil = a.utilization ?? 0;
  const bUtil = b.utilization ?? 0;

  return bUtil - aUtil;
}

type FleetCapacityReportTableProps = {
  vehicles: FleetManagementRow[];
  capacityPerformancePerWorkplace: capacityPerformanceWorkplace[];
  /** Vehicle IDs to show as selected in the table (e.g. from step 1 fleet fetch) */
  selectedVehicleIds?: number[];
  /** Called when user changes row selection; parent should show Calculate and use new ids for next refetch */
  onSelectionChange?: (selectedIds: number[]) => void;
};

function buildUtilizationMap(
  capacityPerformancePerWorkplace: capacityPerformanceWorkplace[]
): Map<number, number> {
  const map = new Map<number, number>();
  capacityPerformancePerWorkplace.forEach(item => {
    map.set(item.bookingObject, item.capacityRate);
  });
  return map;
}

export function FleetCapacityReportTable({
  vehicles,
  capacityPerformancePerWorkplace,
  selectedVehicleIds = [],
  onSelectionChange
}: Readonly<FleetCapacityReportTableProps>): React.ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();

  const [enlargedImagePaths, setEnlargedImagePaths] = useState<string[] | null>(null);

  // Column visibility: single source of truth so MRT's column visibility dropdown and our custom panel stay in sync
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    picturePaths: false
  });

  const rowSelection = useMemo(
    () => Object.fromEntries(selectedVehicleIds.map(id => [id.toString(), true])),
    [selectedVehicleIds]
  );

  const handleRowSelectionChange = useCallback(
    (
      updaterOrValue:
        | MRT_RowSelectionState
        | ((old: MRT_RowSelectionState) => MRT_RowSelectionState)
    ) => {
      const nextSelection =
        typeof updaterOrValue === "function" ? updaterOrValue(rowSelection) : updaterOrValue;
      onSelectionChange?.(rowSelectionToIds(nextSelection));
    },
    [rowSelection, onSelectionChange]
  );

  const rows: FleetReportRow[] = useMemo(() => {
    const utilizationMap = buildUtilizationMap(capacityPerformancePerWorkplace);
    return vehicles.map(v => ({
      ...v,
      utilization: utilizationMap.get(v.id)
    }));
  }, [vehicles, capacityPerformancePerWorkplace]);

  const selectedSet = useMemo(() => new Set(selectedVehicleIds), [selectedVehicleIds]);

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => compareFleetReportRows(a, b, selectedSet));
  }, [rows, selectedSet]);

  const columns = useMemo<MRT_ColumnDef<FleetReportRow>[]>(
    () => [
      {
        accessorKey: "picturePaths",
        header: t("Image"),
        size: 100,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) =>
          renderTableItemMultiImages(
            cell.row.original.picturePaths,
            "fleet-report-vehicle-image",
            80,
            paths => setEnlargedImagePaths(paths)
          )
      },
      { accessorKey: "name", header: t("Name"), size: 180 },
      {
        accessorKey: "utilization",
        header: t("Utilization"),
        enableColumnFilter: false,
        size: 100,
        Cell: ({ cell }) => {
          const val = cell.getValue<number | undefined>();
          if (val == null) return "—";
          return `${(val * 100).toFixed(1)}%`;
        },
        muiTableBodyCellProps: { align: "center" }
      },
      { accessorKey: "licensePlate", header: t("LicensePlate"), size: 120 },
      { accessorKey: "brandModel", header: t("Brand Model"), size: 140 },
      { accessorKey: "vehicleType", header: t("Vehicle Type"), size: 120 },
      {
        accessorKey: "properties",
        header: t("Properties"),
        size: 150,
        enableColumnFilter: false,
        Cell: ({ row }) => renderFleetPropertiesCell(row.original.properties ?? [])
      },
      {
        accessorKey: "fuel",
        header: t("Fuel Type"),
        size: 120,
        Cell: ({ row }) => renderFuelCell(row.original),
        filterSelectOptions: Object.entries(FleetFuelType).map(fuel => ({
          label: t(fuel[1]),
          value: fuel[1],
          key: fuel[0]
        })),
        filterVariant: "multi-select",
        filterFn: "arrIncludesSome"
      },
      { accessorKey: "reach", header: t("Reach"), size: 80, filterVariant: "range" },
      {
        accessorKey: "numSeats",
        header: t("Number of Seats"),
        size: 100,
        filterVariant: "range"
      },
      {
        id: "location",
        header: t("Location"),
        size: 160,
        accessorFn: row => row.location?.name ?? "",
        Cell: ({ row }) => row.original.location?.name ?? "—"
      }
    ],
    [t]
  );
  const seeditMrtOptions = useSeeditMrtTableOptions<FleetReportRow>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    columns,
    data: sortedRows,
    getRowId: row => row.id?.toString(),
    enableEditing: false,
    enableRowSelection: true,
    enablePagination: true,
    enableSorting: true,
    enableBottomToolbar: true,
    enableTopToolbar: true,
    enableFullScreenToggle: false,
    autoResetPageIndex: false,
    positionToolbarAlertBanner: "bottom",
    state: { rowSelection },
    muiPaginationProps: {
      rowsPerPageOptions: [5, 10, 15],
      variant: "outlined"
    },
    onRowSelectionChange: handleRowSelectionChange,
    onColumnVisibilityChange: updater => {
      setColumnVisibility(prev => (typeof updater === "function" ? updater(prev) : updater));
    },
    localization: {
      ...commonLocalization(t),
      noRecordsToDisplay: t("There are no vehicles")
    },
    initialState: withSeeditMrtInitialState({
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 10, pageIndex: 0 },
      columnVisibility
    }),
    muiToolbarAlertBannerProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        "& .MuiAlert-message": { display: "flex", alignItems: "center", minWidth: 0 },
        "& .MuiButton-root": { flexShrink: 0, px: 1.5, py: 0.625 }
      }
    }
  });

  return (
    <>
      <Paper variant="outlined" sx={{ width: "100%", overflow: "auto", mt: 2 }}>
        <MaterialReactTable table={table} />
      </Paper>

      <EnlargedImageCarouselDialog
        isOpen={enlargedImagePaths !== null}
        picturePaths={enlargedImagePaths}
        handleClose={() => setEnlargedImagePaths(null)}
        isLoading={false}
      />
    </>
  );
}
